import React from 'react'
import { SIDE_MENU_COLLAPSED_KEY } from 'shared/constants/storage'

// Кто свернул меню: раздел сам или пользователь кнопкой
type TCollapsedBy = 'auto' | 'user' | null

const readSavedCollapsedBy = (): TCollapsedBy => {
  try {
    const value = localStorage.getItem(SIDE_MENU_COLLAPSED_KEY)
    if (value === 'user') return 'user'
    // '1' осталось от версии, где причину не хранили: почти всегда меню сворачивал раздел
    return value === 'auto' || value === '1' ? 'auto' : null
  } catch {
    return null
  }
}

const saveCollapsedBy = (collapsedBy: TCollapsedBy) => {
  try {
    localStorage.setItem(SIDE_MENU_COLLAPSED_KEY, collapsedBy ?? '0')
  } catch {
    // localStorage недоступен — положение продержится до перезагрузки
  }
}

// Свёрнутое разделом меню раскрывается само, как только место появилось;
// свёрнутое вручную ждёт, пока его раскроют
const resolveCollapsedBy = (
  collapsedBy: TCollapsedBy,
  isAutoCollapsed: boolean,
  hasRoom: boolean
): TCollapsedBy => {
  if (collapsedBy === 'user') return 'user'
  if (isAutoCollapsed) return 'auto'
  return collapsedBy === 'auto' && !hasRoom ? 'auto' : null
}

// Свёрнутость бокового меню, запоминается в localStorage. Разделы из autoCollapseKeys
// при входе сворачивают меню, если рядом с раскрытым им не хватает места (hasRoom), —
// после выхода из них меню остаётся свёрнутым, пока его не раскроют или место не появится
export const useSideMenuCollapse = (
  current: string,
  autoCollapseKeys: string[] = [],
  hasRoom = false
) => {
  const isAutoCollapsed = !hasRoom && autoCollapseKeys.includes(current)
  // Сразу в итоговом положении, чтобы при открытии раздела по ссылке меню не менялось на глазах
  const [collapsedBy, setCollapsedBy] = React.useState(() =>
    resolveCollapsedBy(readSavedCollapsedBy(), isAutoCollapsed, hasRoom)
  )

  React.useEffect(() => {
    setCollapsedBy((prev) => resolveCollapsedBy(prev, isAutoCollapsed, hasRoom))
  }, [current, isAutoCollapsed, hasRoom])

  React.useEffect(() => {
    saveCollapsedBy(collapsedBy)
  }, [collapsedBy])

  const toggleCollapsed = () => setCollapsedBy(collapsedBy ? null : 'user')

  return { isCollapsed: collapsedBy !== null, toggleCollapsed }
}
